import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CostCenterOptionLabel from '../../../../../documentostributarios/costCenters/CostCenterOptionLabel';
import './JournalEntryCostCenterSelect.scss';

const MENU_Z_INDEX = 100100;

const JournalEntryCostCenterSelect = ({
  options = [],
  value,
  displayLabel,
  onChange,
  placeholder = 'Seleccionar...',
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const skipOutsideCloseRef = useRef(false);

  const selected = options.find((o) => String(o.id) === String(value));
  const fallbackLabel = !selected && value && displayLabel ? displayLabel : null;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, search]);

  const updateMenuPosition = () => {
    const trigger = rootRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const gap = 6;
    const estimatedHeight = Math.min(280, Math.max(filtered.length, 1) * 44 + 52);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;

    setMenuStyle({
      position: 'fixed',
      left: rect.left,
      width: Math.max(rect.width, 220),
      zIndex: MENU_Z_INDEX,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
    });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    updateMenuPosition();
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return undefined;

    const onDocPointerDown = (e) => {
      if (skipOutsideCloseRef.current) {
        skipOutsideCloseRef.current = false;
        return;
      }
      if (
        rootRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      setOpen(false);
      setSearch('');
    };

    const onScrollOrResize = () => updateMenuPosition();
    const timerId = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDocPointerDown, true);
    }, 0);
    window.addEventListener('resize', onScrollOrResize);
    window.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      window.removeEventListener('resize', onScrollOrResize);
      window.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [open, filtered.length]);

  const toggleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    skipOutsideCloseRef.current = true;
    setOpen((v) => !v);
  };

  const handleSelect = (id) => {
    onChange(id);
    setOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onChange('');
    setOpen(false);
    setSearch('');
  };

  const menu =
    open && !disabled && menuStyle ? (
      <div
        ref={menuRef}
        className="journal-entry-cc-select__menu"
        style={menuStyle}
        role="listbox"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="journal-entry-cc-select__search-wrap">
          <input
            type="text"
            className="journal-entry-cc-select__search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPointerDown={(e) => e.stopPropagation()}
            autoFocus
          />
        </div>
        <ul className="journal-entry-cc-select__list">
          {filtered.length === 0 ? (
            <li className="journal-entry-cc-select__empty">
              {options.length === 0 ? 'No hay centros de costo' : 'Sin resultados'}
            </li>
          ) : (
            filtered.map((opt) => (
              <li key={opt.id}>
                <button
                  type="button"
                  className={`journal-entry-cc-select__option${String(value) === String(opt.id) ? ' journal-entry-cc-select__option--active' : ''}`}
                  onClick={() => handleSelect(opt.id)}
                >
                  <CostCenterOptionLabel name={opt.name} icon={opt.icon} color={opt.color} />
                </button>
              </li>
            ))
          )}
        </ul>
      </div>
    ) : null;

  return (
    <div
      ref={rootRef}
      className={`journal-entry-cc-select${open ? ' journal-entry-cc-select--open' : ''}${disabled ? ' journal-entry-cc-select--disabled' : ''}`}
    >
      <button
        type="button"
        className="journal-entry-cc-select__trigger"
        onClick={toggleOpen}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="journal-entry-cc-select__value">
          {selected ? (
            <CostCenterOptionLabel
              name={selected.name}
              icon={selected.icon}
              color={selected.color}
            />
          ) : fallbackLabel ? (
            <span className="journal-entry-cc-select__fallback-label">{fallbackLabel}</span>
          ) : (
            <span className="journal-entry-cc-select__placeholder">{placeholder}</span>
          )}
        </span>
        <span className="journal-entry-cc-select__actions">
          {selected && (
            <span
              className="journal-entry-cc-select__clear"
              role="button"
              tabIndex={-1}
              onPointerDown={handleClear}
              aria-label="Quitar selección"
            >
              ×
            </span>
          )}
          <span className={`journal-entry-cc-select__chevron${open ? ' journal-entry-cc-select__chevron--up' : ''}`} />
        </span>
      </button>
      {typeof document !== 'undefined' && menu
        ? createPortal(menu, document.body)
        : null}
    </div>
  );
};

export default JournalEntryCostCenterSelect;
